import {
  assistantGeneratingBubbleController,
  assistantTextBubbleController,
  assistantThinkingBubbleController,
  assistantToolBubbleController,
  userMessageBubbleController,
} from "../controllers/index.js";
import { applyBubbleHtml } from "./bubbleUiDom.js";

function createBubbleId() {
  return `bubble-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function createMessageId() {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function buildLegacyBubbleConfig(preset, props = {}) {
  switch (preset) {
    case "assistantText":
      return assistantTextBubbleController.createBubble(props);
    case "assistantThinking":
      return assistantThinkingBubbleController.createBubble(props);
    case "assistantFunctionCall":
      return assistantToolBubbleController.createToolBubble(props);
    case "userText":
      return userMessageBubbleController.createBubble({ ...props, kind: "text" });
    case "userImages":
      return userMessageBubbleController.createBubble({
        ...props,
        kind: "images",
      });
    case "userFiles":
      return userMessageBubbleController.createBubble({ ...props, kind: "files" });
    case "generating":
      return assistantGeneratingBubbleController.createBubble(props);
    default:
      return assistantTextBubbleController.createBubble(props);
  }
}

export class BubbleUiSystem {
  constructor(options = {}) {
    this.bubbles = [];
    this.bubbleMap = new Map();
    this.onBubblesChange = null;
    this.parentElement = null;
    this.workspaceManager = null;
    this.toolSystem = null;
    this.aiToolSystem = null;
    this.aiInferenceSystem = null;
    this.aiChatInterface = null;

    // Global follow policy for the chat scroll container.
    // When enabled and the model is generating, resize changes force an
    // instant scroll-to-bottom (no "near bottom" auto detection).
    this.generationFollowEnabled = true;
    this.isModelGenerating = false;

    this.resizeObserver = null;
    this._isInFollowResizeCallback = false;
    this._followFrameId = null;
    this.eventHandlers = {
      onImageClick: null,
      onFileClick: null,
      onOpenFileByPath: null,
      onOpenUrl: null,
      onBubbleClickAction: null,
      onBubbleContextMenu: null,
      ...options,
    };

    this.streamingManager = {
      elements: new Map(),
      bubbleHtmlByBubbleId: new Map(),
      registerElement: (bubbleId, type, element) => {
        const entry = this._ensureStreamingEntry(bubbleId);
        entry[type] = element;
        if (type === "rootElement") {
          const cachedHtml = this.streamingManager.bubbleHtmlByBubbleId.get(bubbleId);
          if (typeof cachedHtml === "string") {
            applyBubbleHtml(element, cachedHtml);
          }
        }
      },
      unregisterElement: (bubbleId, type) => {
        const entry = this.streamingManager.elements.get(bubbleId);
        if (!entry) {
          return;
        }
        delete entry[type];
        if (Object.keys(entry).length === 0) {
          this.streamingManager.elements.delete(bubbleId);
        }
      },
      updateBubbleHtml: (bubbleId, html, rawContent) => {
        const bubble = this.bubbleMap.get(bubbleId);
        if (!bubble) {
          return;
        }
        this.streamingManager.bubbleHtmlByBubbleId.set(bubbleId, html || "");
        const entry = this._ensureStreamingEntry(bubbleId);
        if (rawContent !== undefined) {
          entry.rawContent = rawContent;
        }
        entry.currentHtml = html || "";
        if (entry.rootElement) {
          applyBubbleHtml(entry.rootElement, html || "");
        }
        bubble.html = html || "";
        this._applyBubbleChrome(bubble);
        this._invokeBubbleHook(bubble, "onBubbleRendered");
      },
      updateToolUiHtml: (bubbleId, html) => {
        this.streamingManager.updateBubbleHtml(bubbleId, html);
      },
      updateBubbleDurationText: (bubbleId, text) => {
        const entry = this.streamingManager.elements.get(bubbleId);
        if (!entry) {
          return;
        }
        entry.currentDurationDisplay = text;
        const durationElement = entry.rootElement?.querySelector?.(
          "[data-bubble-duration]"
        );
        if (durationElement) {
          durationElement.textContent = text;
        }
      },
    };

    this.rootElement = document.createElement("div");
    this.rootElement.className = "ai-chat-area";
    this.rootElement.dataset.bubbleUiRoot = "true";

    this.contentElement = document.createElement("div");
    this.contentElement.className = "ai-chat-area-content";
    this.rootElement.appendChild(this.contentElement);

    // Visual spacer at the end of the scroll content so the last
    // bubble isn't flush against the bottom.
    this.bottomSpacerElement = document.createElement("div");
    this.bottomSpacerElement.className = "ai-chat-area-bottom-spacer";
    this.bottomSpacerElement.style.display = "none";
    this.contentElement.appendChild(this.bottomSpacerElement);

    this._syncDomContextRefs();
    this._setupResizeObserver();
  }

  setSystemContext(nextContext = {}) {
    if (!nextContext || typeof nextContext !== "object") {
      return;
    }

    if (Object.prototype.hasOwnProperty.call(nextContext, "workspaceManager")) {
      this.workspaceManager = nextContext.workspaceManager || null;
    }
    if (Object.prototype.hasOwnProperty.call(nextContext, "toolSystem")) {
      this.toolSystem = nextContext.toolSystem || null;
    }
    if (Object.prototype.hasOwnProperty.call(nextContext, "aiToolSystem")) {
      this.aiToolSystem = nextContext.aiToolSystem || null;
    }
    if (Object.prototype.hasOwnProperty.call(nextContext, "aiInferenceSystem")) {
      this.aiInferenceSystem = nextContext.aiInferenceSystem || null;
    }
    if (Object.prototype.hasOwnProperty.call(nextContext, "aiChatInterface")) {
      this.aiChatInterface = nextContext.aiChatInterface || null;
    }

    this._syncDomContextRefs();
  }

  _maybeFollow() {
    if (!this.isModelGenerating) return;
    if (!this.generationFollowEnabled) return;
    this._scheduleFollowToBottom();
  }

  setEventHandlers(nextHandlers = {}) {
    this.eventHandlers = {
      ...this.eventHandlers,
      ...nextHandlers,
    };
  }

  setOnBubblesChange(handler) {
    this.onBubblesChange = typeof handler === "function" ? handler : null;
    this._emitBubblesChange();
  }

  setGenerationFollowEnabled(enabled) {
    this.generationFollowEnabled = Boolean(enabled);
    // When user explicitly re-enables follow (lock mode), snap to bottom immediately
    // even if the model isn't currently streaming.
    if (this.generationFollowEnabled) {
      this.scrollToBottomInstant();
    }
  }

  setIsModelGenerating(isGenerating) {
    this.isModelGenerating = Boolean(isGenerating);
    if (this.isModelGenerating && this.generationFollowEnabled) {
      this._scheduleFollowToBottom();
    }
  }

  getRootElement() {
    return this.rootElement;
  }

  mount(parentElement) {
    if (!parentElement || this.parentElement === parentElement) {
      return;
    }
    this.unmount();
    this.parentElement = parentElement;
    parentElement.replaceChildren(this.rootElement);
  }

  unmount() {
    if (!this.parentElement) {
      return;
    }
    if (this.rootElement.parentNode === this.parentElement) {
      this.parentElement.removeChild(this.rootElement);
    }
    this.parentElement = null;
  }

  destroy() {
    if (this._followFrameId != null) {
      if (typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(this._followFrameId);
      } else {
        clearTimeout(this._followFrameId);
      }
      this._followFrameId = null;
    }
    this.clearBubbles();
    this.unmount();
  }

  getBubbles() {
    return this.bubbles;
  }

  getBubbleContent(bubbleId) {
    const bubble = this.bubbleMap.get(bubbleId);
    if (bubble && typeof bubble.getContent === "function") {
      return bubble.getContent(bubble);
    }
    return bubble?.content || "";
  }

  getBubbleRootElement(bubbleId) {
    return this.bubbleMap.get(bubbleId)?.rootElement || null;
  }

  queryBubble(bubbleId, selector) {
    if (!selector) return null;
    return this.getBubbleRootElement(bubbleId)?.querySelector?.(selector) || null;
  }

  queryAllBubble(bubbleId, selector) {
    if (!selector) return [];
    return Array.from(
      this.getBubbleRootElement(bubbleId)?.querySelectorAll?.(selector) || []
    );
  }

  patchBubbleDom(bubbleId, patcher) {
    const rootElement = this.getBubbleRootElement(bubbleId);
    if (!rootElement || typeof patcher !== "function") {
      return false;
    }

    patcher(rootElement, this._buildBubbleUiApi(bubbleId));
    return true;
  }

  getBubbleUiApi(bubbleId) {
    return this._buildBubbleUiApi(bubbleId);
  }

  _updateBubbleRecord(bubbleId, updates = {}, options = {}) {
    const bubble = this.bubbleMap.get(bubbleId);
    if (!bubble) {
      return null;
    }

    const previousBubble = { ...bubble };
    const renderHtml = options?.renderHtml !== false;
    const preserveExistingHtml = options?.preserveExistingHtml === true;

    const updatedBubble =
      options?.useUpdateFunction === false
        ? {
            ...bubble,
            ...updates,
          }
        : typeof bubble.update === "function"
          ? bubble.update(bubble, updates)
          : { ...bubble, ...updates };

    if (preserveExistingHtml) {
      updatedBubble.html = bubble.html;
      if (!Object.prototype.hasOwnProperty.call(updates, "toolUiHtml")) {
        updatedBubble.toolUiHtml = bubble.toolUiHtml;
      }
    } else if (typeof updatedBubble.html !== "string") {
      updatedBubble.html =
        typeof updatedBubble.toolUiHtml === "string" ? updatedBubble.toolUiHtml : "";
    }

    updatedBubble.rootElement = bubble.rootElement;
    this.bubbleMap.set(bubbleId, updatedBubble);
    this.bubbles = this.bubbles.map((entry) =>
      entry.bubbleId === bubbleId ? updatedBubble : entry
    );

    if (renderHtml) {
      this.streamingManager.updateBubbleHtml(
        bubbleId,
        updatedBubble.html,
        updatedBubble.content
      );
    }

    if (updatedBubble.isComplete && !previousBubble.isComplete) {
      this._invokeBubbleHook(updatedBubble, "onBubbleCompleted");
    } else {
      this._invokeBubbleHook(updatedBubble, "onBubbleUpdated");
    }

    if (options?.follow !== false) {
      this._maybeFollow();
    }

    return updatedBubble;
  }

  createBubble(preset, props = {}) {
    const bubbleConfig =
      typeof preset === "string"
        ? buildLegacyBubbleConfig(preset, props)
        : { ...preset, ...props };

    if (!bubbleConfig) {
      return null;
    }

    const newBubble = {
      bubbleId: bubbleConfig.bubbleId || createBubbleId(),
      messageId: bubbleConfig.messageId ?? props.messageId ?? createMessageId(),
      ...bubbleConfig,
    };

    if (typeof newBubble.getContent !== "function") {
      newBubble.getContent = (bubble) => bubble?.content || "";
    }
    if (typeof newBubble.html !== "string") {
      newBubble.html =
        typeof newBubble.toolUiHtml === "string" ? newBubble.toolUiHtml : "";
    }

    const rowElement = document.createElement("div");
    rowElement.dataset.bubbleId = newBubble.bubbleId;
    rowElement.dataset.messageId = newBubble.messageId;
    rowElement.dataset.bubbleUiRow = "true";
    rowElement.className = "chat-bubble";

    // Keep spacer at the end; insert bubbles before it.
    if (this.bottomSpacerElement) {
      this.contentElement.insertBefore(rowElement, this.bottomSpacerElement);
    } else {
      this.contentElement.appendChild(rowElement);
    }
    newBubble.rootElement = rowElement;
    this.bubbles = [...this.bubbles, newBubble];
    this.bubbleMap.set(newBubble.bubbleId, newBubble);

    const entry = this._ensureStreamingEntry(newBubble.bubbleId);
    entry.rootElement = rowElement;
    this._attachDomContextToBubbleRow(rowElement, newBubble);

    this.streamingManager.updateBubbleHtml(
      newBubble.bubbleId,
      newBubble.html,
      newBubble.content
    );
    this._installBubbleContextMenuHandler(newBubble);
    this._invokeBubbleHook(newBubble, "onBubbleInit");
    this._invokeBubbleHook(newBubble, "onBubbleRendered");
    this._maybeFollow();
    this._emitBubblesChange();
    return newBubble.bubbleId;
  }

  updateBubble(bubbleId, updates = {}) {
    this._updateBubbleRecord(bubbleId, updates, {
      renderHtml: true,
      useUpdateFunction: true,
    });
  }

  updateBubbleState(bubbleId, updates = {}) {
    this._updateBubbleRecord(bubbleId, updates, {
      renderHtml: false,
      preserveExistingHtml: true,
      useUpdateFunction: false,
    });
  }

  updateBubbleHtml(bubbleId, html, rawContent) {
    this.streamingManager.updateBubbleHtml(bubbleId, html, rawContent);
    this._maybeFollow();
  }

  updateToolUiHtml(bubbleId, html) {
    this.streamingManager.updateToolUiHtml(bubbleId, html);
    this._maybeFollow();
  }

  updateBubbleDurationText(bubbleId, text) {
    this.streamingManager.updateBubbleDurationText(bubbleId, text);
    this._maybeFollow();
  }

  removeBubble(bubbleId) {
    const bubble = this.bubbleMap.get(bubbleId);
    if (!bubble) {
      return;
    }

    this._invokeBubbleHook(bubble, "onBubbleRemoved");
    bubble.rootElement?.remove?.();
    this.streamingManager.bubbleHtmlByBubbleId.delete(bubbleId);
    this.streamingManager.elements.delete(bubbleId);
    this.bubbleMap.delete(bubbleId);
    this.bubbles = this.bubbles.filter((entry) => entry.bubbleId !== bubbleId);
    this._emitBubblesChange();

    this._maybeFollow();
  }

  clearBubbles() {
    [...this.bubbles].forEach((bubble) => {
      this._invokeBubbleHook(bubble, "onBubbleRemoved");
      bubble.rootElement?.remove?.();
    });
    this.bubbles = [];
    this.bubbleMap.clear();
    this.streamingManager.elements.clear();
    this.streamingManager.bubbleHtmlByBubbleId.clear();
    this._emitBubblesChange();

    this._maybeFollow();
  }

  scrollToBottomAndEnable() {
    this.scrollToBottomInstant();
  }

  scrollToBottomInstant() {
    // Ensure instant jump without any animation.
    this.rootElement.scrollTop = this.rootElement.scrollHeight;
  }

  _scheduleFollowToBottom() {
    if (this._followFrameId != null) {
      return;
    }

    const scheduleFrame =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame
        : (callback) => setTimeout(callback, 16);

    this._followFrameId = scheduleFrame(() => {
      this._followFrameId = null;
      if (!this.isModelGenerating || !this.generationFollowEnabled) {
        return;
      }
      this.scrollToBottomInstant();
    });
  }

  /**
   * Scroll so a given point on a bubble aligns with a given point in viewport.
   * @param {string} bubbleId
   * @param {{
   *  bubbleAnchor?: "top"|"center"|"bottom",
   *  containerAnchor?: "top"|"center"|"bottom",
   *  offsetPx?: number,
   * }} options
   */
  scrollToBubbleRow(bubbleId, options = {}) {
    const bubble = this.bubbleMap.get(bubbleId);
    const bubbleEl = bubble?.rootElement || null;
    if (!bubbleEl) return false;

    const bubbleAnchor = options?.bubbleAnchor || "top";
    const containerAnchor = options?.containerAnchor || "center";
    const offsetPx = Number.isFinite(options?.offsetPx)
      ? options.offsetPx
      : 0;

    const containerEl = this.rootElement;
    const containerRect = containerEl.getBoundingClientRect();
    const containerHeight = containerEl.clientHeight || containerRect.height;

    const bubbleRect = bubbleEl.getBoundingClientRect();

    const bubbleAnchorViewportY = (() => {
      const yTop = bubbleRect.top - containerRect.top;
      const yCenter = bubbleRect.top + bubbleRect.height / 2 - containerRect.top;
      const yBottom = bubbleRect.bottom - containerRect.top;
      if (bubbleAnchor === "top") return yTop;
      if (bubbleAnchor === "bottom") return yBottom;
      return yCenter; // center
    })();

    const targetViewportY = (() => {
      if (containerAnchor === "top") return 0 + offsetPx;
      if (containerAnchor === "bottom") return containerHeight + offsetPx;
      return containerHeight / 2 + offsetPx; // center
    })();

    const delta = bubbleAnchorViewportY - targetViewportY;
    const nextScrollTop = this.rootElement.scrollTop + delta;

    const maxScrollTop = Math.max(0, this.rootElement.scrollHeight - containerEl.clientHeight);
    this.rootElement.scrollTop = Math.min(maxScrollTop, Math.max(0, nextScrollTop));
    return true;
  }

  _ensureStreamingEntry(bubbleId) {
    if (!this.streamingManager.elements.has(bubbleId)) {
      this.streamingManager.elements.set(bubbleId, {});
    }
    return this.streamingManager.elements.get(bubbleId);
  }

  _setupResizeObserver() {
    if (this.resizeObserver || typeof ResizeObserver === "undefined") {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this._isInFollowResizeCallback) return;
      if (!this.isModelGenerating || !this.generationFollowEnabled) return;

      // Prevent re-entrant layout thrashing when scrollTop mutation triggers another resize.
      this._isInFollowResizeCallback = true;
      try {
        this._scheduleFollowToBottom();
      } finally {
        this._isInFollowResizeCallback = false;
      }
    });

    // Observing the content element captures "bubbles grew/shrank" without relying
    // on scroll position heuristics.
    this.resizeObserver.observe(this.contentElement);
  }

  _emitBubblesChange() {
    this.onBubblesChange?.(this.bubbles.length);
    if (this.bottomSpacerElement) {
      this.bottomSpacerElement.style.display =
        this.bubbles.length > 0 ? "" : "none";
    }
  }

  _buildDomContext() {
    return {
      bubbleUiSystem: this,
      workspaceManager: this.workspaceManager || null,
      toolSystem: this.toolSystem || null,
      aiToolSystem: this.aiToolSystem || null,
      aiInferenceSystem: this.aiInferenceSystem || null,
      aiChatInterface: this.aiChatInterface || null,
      openFileByPath: (path) => this.eventHandlers.onOpenFileByPath?.(path),
      openUrl: (url) => this.eventHandlers.onOpenUrl?.(url),
      bubbleClickAction: (action) =>
        this.eventHandlers.onBubbleClickAction?.(action),
      getBubbles: () => this.getBubbles(),
    };
  }

  _attachDomContextToRoot() {
    if (!this.rootElement) {
      return;
    }
    this.rootElement.dataset.bubbleUiRoot = "true";
    this.rootElement.bubbleUiSystem = this;
    this.rootElement.bubbleUiContext = this._buildDomContext();
  }

  _attachDomContextToBubbleRow(rowElement, bubble = null) {
    if (!rowElement) {
      return;
    }
    rowElement.dataset.bubbleUiRow = "true";
    rowElement.bubbleUiSystem = this;
    rowElement.bubbleUiRootElement = this.rootElement || null;
    rowElement.bubbleUiContext =
      this.rootElement?.bubbleUiContext || this._buildDomContext();
    rowElement.bubbleRecord = bubble || null;
  }

  _syncDomContextRefs() {
    this._attachDomContextToRoot();
    this.bubbles.forEach((bubble) => {
      this._attachDomContextToBubbleRow(bubble?.rootElement, bubble);
    });
  }

  _applyBubbleChrome(bubble) {
    const rootElement = bubble?.rootElement;
    if (!rootElement) {
      return;
    }

    const classNames = [
      "chat-bubble",
      `chat-bubble-${bubble.role === "user" ? "user" : "assistant"}`,
    ];

    const isToolBubble =
      bubble?.functionName ||
      bubble?.toolCallId ||
      bubble?.toolUiHtml != null;
    if (isToolBubble) {
      classNames.push("chat-bubble-function-call");
      if (bubble?.bubbleStyle === "bubble") {
        classNames.push("chat-bubble-bubble");
        classNames.push("chat-bubble-function-call-bubble");
      } else {
        classNames.push("chat-bubble-function-call-simple");
      }
    }

    if (bubble.isDisabled) {
      classNames.push("chat-bubble-disabled");
    }

    rootElement.className = classNames.join(" ");
    rootElement.style.pointerEvents = bubble.isDisabled ? "none" : "";
  }

  _installBubbleContextMenuHandler(bubble) {
    const rootElement = bubble?.rootElement;
    if (!rootElement || rootElement.dataset.contextMenuBound === "true") {
      return;
    }

    rootElement.dataset.contextMenuBound = "true";
    rootElement.addEventListener("contextmenu", (event) => {
      if (!this.eventHandlers.onBubbleContextMenu) {
        return;
      }

      const explicitContextTarget = event.target?.closest?.(
        "[data-bubble-context-menu-target]"
      );
      const hasExplicitTargets = rootElement.querySelector(
        "[data-bubble-context-menu-target]"
      );

      if (hasExplicitTargets && !explicitContextTarget) {
        return;
      }

      const latestBubble = this.bubbleMap.get(bubble.bubbleId) || bubble;
      this.eventHandlers.onBubbleContextMenu(event, latestBubble);
    });
  }

  _buildBubbleUiApi(bubbleId) {
    const getBubble = () => this.bubbleMap.get(bubbleId);
    const getRootElement = () => getBubble()?.rootElement || null;

    return {
      updateBubbleHtml: (html, rawContent) =>
        this.streamingManager.updateBubbleHtml(bubbleId, html, rawContent),
      updateToolUiHtml: (html) =>
        this.streamingManager.updateToolUiHtml(bubbleId, html),
      clearBubbleHtml: () => this.streamingManager.updateBubbleHtml(bubbleId, ""),
      clearToolUi: () => this.streamingManager.updateToolUiHtml(bubbleId, ""),
      updateBubbleDurationText: (text) =>
        this.streamingManager.updateBubbleDurationText(bubbleId, text),
      patchBubbleDom: (patcher) => this.patchBubbleDom(bubbleId, patcher),
      updateBubbleState: (updates) => this.updateBubbleState(bubbleId, updates),
      getRootElement,
      query: (selector) => getRootElement()?.querySelector?.(selector) || null,
      queryAll: (selector) =>
        Array.from(getRootElement()?.querySelectorAll?.(selector) || []),
      handleImageClick: (bubbleIdValue, imageIndex, allImages) =>
        this.eventHandlers.onImageClick?.(bubbleIdValue, imageIndex, allImages),
      handleFileClick: (bubbleIdValue, fileIndex, allFiles) =>
        this.eventHandlers.onFileClick?.(bubbleIdValue, fileIndex, allFiles),
      handleOpenFileByPath: (path) =>
        this.eventHandlers.onOpenFileByPath?.(path),
      handleOpenUrl: (url) => this.eventHandlers.onOpenUrl?.(url),
      handleBubbleClickAction: (action) =>
        this.eventHandlers.onBubbleClickAction?.(action),
      updateBubble: (nextBubbleId, updates) => this.updateBubble(nextBubbleId, updates),
      scrollToBottomInstant: () => this.scrollToBottomInstant(),
      scrollToBubbleRow: (targetBubbleId, scrollOptions) =>
        this.scrollToBubbleRow(targetBubbleId, scrollOptions),
      setGenerationFollowEnabled: (enabled) =>
        this.setGenerationFollowEnabled(enabled),
    };
  }

  _invokeBubbleHook(bubble, hookName) {
    if (!bubble?.bubbleId) {
      return;
    }
    const hook = bubble[hookName];
    if (typeof hook !== "function") {
      return;
    }
    hook(bubble, this._buildBubbleUiApi(bubble.bubbleId));
  }
}

export default BubbleUiSystem;
